import { Box } from '@ublend-npm/aulaui-core';
import React from 'react';
import { ResponsiveAssignment } from '@core/assignments/types';
import AssignmentCard from '../AssignmentCard/AssignmentCard';
import { createStyles } from './AssignmentList.styles';
import { useSelector } from 'react-redux';
import { getLtiProviders } from '@core/selectors/lti';
import { AulaState } from '@core/types/state';

type Props = {
  assignments: ResponsiveAssignment[];
  spaceId: string;
  isEducator?: boolean;
  highlightedAssignmentId?: string | null;
  onMaterialClick?: (
    materialId: string,
    materialTitle?: string,
    assignmentId?: string
  ) => void;
  onTurnitinClick?: (assignment: ResponsiveAssignment) => void;
};

type AssignmentWithParent = ResponsiveAssignment & {
  parentAssignment?: ResponsiveAssignment;
};

export const getStudentAssignments = (
  assignments: ResponsiveAssignment[]
): AssignmentWithParent[] => {
  const assignmentsById = new Map<string, ResponsiveAssignment>();

  assignments.forEach((assignment) => {
    if (assignment.id) assignmentsById.set(assignment.id, assignment);
    if (assignment.objectId) {
      assignmentsById.set(assignment.objectId, assignment);
    }
  });

  const parentAssignmentIds = new Set(
    assignments
      .map(({ parentAssignmentId }) => parentAssignmentId)
      .filter((id): id is string => Boolean(id))
  );

  return assignments
    .filter(
      ({ id, objectId }) =>
        !parentAssignmentIds.has(id) &&
        (!objectId || !parentAssignmentIds.has(objectId))
    )
    .map((assignment) => {
      if (!assignment.parentAssignmentId) return assignment;

      return {
        ...assignment,
        parentAssignment:
          (assignment as AssignmentWithParent).parentAssignment ??
          assignmentsById.get(assignment.parentAssignmentId),
      };
    });
};

const AssignmentList = ({
  assignments,
  spaceId,
  isEducator,
  highlightedAssignmentId,
  onMaterialClick,
  onTurnitinClick,
}: Props) => {
  const styles = createStyles();
  const state = useSelector((state) => state);
  const providers = getLtiProviders(state as AulaState);
  const displayedAssignments = isEducator
    ? assignments
    : getStudentAssignments(assignments);
  const isHighlighted = (assignment: ResponsiveAssignment) =>
    assignment.id === highlightedAssignmentId ||
    assignment.objectId === highlightedAssignmentId;

  return (
    <Box sx={styles.listWrapper}>
      {displayedAssignments?.map((assignment) => (
        <AssignmentCard
          assignment={assignment}
          key={assignment.id || assignment.objectId}
          spaceId={spaceId}
          providers={providers}
          isEducator={isEducator}
          highlighted={isHighlighted(assignment)}
          onMaterialClick={onMaterialClick}
          onTurnitinClick={onTurnitinClick}
        />
      ))}
    </Box>
  );
};

export default AssignmentList;
